import React from 'react';
import { Link } from 'react-router-dom';

/**
 * GETTING FOUND — the third thing we do, and the only one that is also a way in.
 *
 * The positioning this section has to hold, because it is easy to get wrong:
 * a redesign already includes this work, so this is not an upsell bolted onto
 * the redesign. It is the smaller, separate job for the business that likes its
 * website and simply is not being found. Those become redesigns later, or they
 * do not, and either is fine.
 *
 * It sits after HowItWorks on purpose. The ask on this page is still "send us
 * your site" and nothing may get in front of it. This runs after the ask has
 * landed, as the other door in.
 *
 * Copy rules inherited from /the-new-era-of-search: plain English, no acronyms,
 * never "optimisation" or "visibility", only Google and ChatGPT named, and no
 * promise that anyone will appear in any answer. No prices anywhere on the site.
 */
export default function GettingFound() {
  return (
    <section className="al-section bg-[#F5F3EF] text-[#0A1A2F]">
      <div className="al-container">
        <div className="al-eyebrow text-[#8C6C2D]"><span aria-hidden />Or just this part</div>

        <div className="mt-8 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <h2
            className="max-w-[15ch] font-serif"
            style={{ fontSize: 'clamp(30px, 4.4vw, 52px)', lineHeight: 1.06, letterSpacing: '-0.028em' }}
          >
            Being found <em className="font-normal text-[#8C6C2D]">is its own job.</em>
          </h2>

          <div>
            <p className="max-w-[54ch] text-[17px] leading-[1.65] text-[#3F3A30]">
              People ask Google and ChatGPT a full question now and get one written answer back,
              naming two or three businesses. That is the shortlist, and there is no page two to
              be on.
            </p>
            <p className="mt-6 max-w-[54ch] text-[17px] leading-[1.65] text-[#3F3A30]">
              A redesign already covers this, because it is the same work. But if you like your
              website and the only problem is that nobody is finding it, we do this part on its
              own and leave the site you have alone.
            </p>

            <Link
              to="/the-new-era-of-search"
              className="group mt-9 inline-flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-[#8C6C2D] transition-colors hover:text-[#0A1A2F]"
            >
              How search changed
              <span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
